// src/components/clients/AISuggestionsPanel.tsx
import { useState } from "react";
import {
  Sparkles,
  FileText,
  Layers,
  ListChecks,
  Loader2,
  BookOpen,
  Send,
} from "lucide-react";
import type { Locale } from "@/shared/i18n/translations";
import { useNoteFormat } from "@/shared/hooks/useNoteFormat";
import {
  NOTE_FORMAT_IDS,
  NOTE_FORMATS,
  type NoteFormatId,
} from "@/shared/lib/noteFormats";

const labels: Record<string, Record<Locale, string>> = {
  title: {
    en: "AI suggestions",
    uk: "AI-підказки",
    pl: "Sugestie AI",
    ro: "Sugestii AI",
  },
  formatLabel: {
    en: "Note format",
    uk: "Формат нотатки",
    pl: "Format notatki",
    ro: "Format notiță",
  },
  // Template strings — {{format}} is substituted at render time with the active note format's shortLabel.
  generateNote: {
    en: "Generate {{format}}",
    uk: "Створити {{format}}",
    pl: "Wygeneruj {{format}}",
    ro: "Generează {{format}}",
  },
  summarize: {
    en: "Summarize conversation",
    uk: "Підсумувати розмову",
    pl: "Podsumuj rozmowę",
    ro: "Rezumă conversația",
  },
  nextSteps: {
    en: "Suggest next steps",
    uk: "Запропонувати наступні кроки",
    pl: "Zaproponuj następne kroki",
    ro: "Sugerează pași",
  },
  generateSummary: {
    en: "Generate summary",
    uk: "Створити підсумок",
    pl: "Wygeneruj podsumowanie",
    ro: "Generează rezumat",
  },
  exportEhr: {
    en: "Export",
    uk: "Експортувати",
    pl: "Eksportuj",
    ro: "Exportă",
  },
  failed: {
    en: "Generation failed.",
    uk: "Не вдалося.",
    pl: "Nie udało się.",
    ro: "A eșuat.",
  },
  confirmOverwrite: {
    en: "Generating a new {{format}} will replace your current notes. Continue?",
    uk: "Створення {{format}} замінить ваші поточні нотатки. Продовжити?",
    pl: "Wygenerowanie {{format}} zastąpi Twoje notatki. Kontynuować?",
    ro: "Generarea {{format}} va înlocui notițele curente. Continuați?",
  },
  confirmOverwriteSummary: {
    en: "Generating a summary will replace the existing one. Continue?",
    uk: "Створення підсумку замінить наявний. Продовжити?",
    pl: "Wygenerowanie podsumowania zastąpi istniejące. Kontynuować?",
    ro: "Generarea unui rezumat îl va înlocui pe cel existent. Continuați?",
  },
};

interface Props {
  sessionId: string;
  locale: Locale;
  notesAreEmpty: boolean;
  hasExistingSummary: boolean;
  onNoteGenerated: (markdown: string) => Promise<void>;
  onSummaryAppended: (text: string) => Promise<void>;
  onNextStepsAppended: (markdown: string) => Promise<void>;
  onSessionSummaryGenerated: () => Promise<void>;
  showError: (msg: string) => void;
  /** Clinical note formats stay in clinical products, never the general workspace. */
  clinicalTemplates?: boolean;
}

type Action = "note" | "summarize" | "next-steps" | "summary";

export function AISuggestionsPanel({
  sessionId,
  locale,
  notesAreEmpty,
  hasExistingSummary,
  onNoteGenerated,
  onSummaryAppended,
  onNextStepsAppended,
  onSessionSummaryGenerated,
  showError,
  clinicalTemplates = true,
}: Props) {
  const [busy, setBusy] = useState<Action | null>(null);
  const format = useNoteFormat();
  // Per-note format override. null → use the profile default (from useNoteFormat).
  // The server's /generate-note already accepts ?format=; this is the UI that
  // finally sends it, so the clinician can pick SOAP/BIRP/DAP/freeform per note.
  const [overrideFormatId, setOverrideFormatId] = useState<NoteFormatId | null>(
    null,
  );
  const effectiveFormatId: NoteFormatId = overrideFormatId ?? format.id;
  const selectedFormat = NOTE_FORMATS[effectiveFormatId];
  const noteShortLabel =
    selectedFormat.shortLabel[locale] ?? selectedFormat.shortLabel.en;

  const t = (key: keyof typeof labels) => {
    const raw = labels[key][locale] ?? labels[key].en;
    return raw.replace("{{format}}", noteShortLabel);
  };

  const run = async (action: Action) => {
    if (action === "note" && !notesAreEmpty) {
      const ok = window.confirm(t("confirmOverwrite"));
      if (!ok) return;
    }
    if (action === "summary" && hasExistingSummary) {
      const ok = window.confirm(t("confirmOverwriteSummary"));
      if (!ok) return;
    }
    setBusy(action);
    try {
      const url =
        action === "note"
          ? `/api/client-sessions/${sessionId}/generate-note?format=${effectiveFormatId}`
          : action === "summarize"
            ? `/api/client-sessions/${sessionId}/summarize`
            : action === "next-steps"
              ? `/api/client-sessions/${sessionId}/suggest-next-steps`
              : `/api/client-sessions/${sessionId}/generate-summary`;
      const { supabase } = await import("@/shared/lib/supabase");
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          locale,
          mode: clinicalTemplates ? "clinical" : "general",
        }),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      if (action === "summary") {
        await onSessionSummaryGenerated();
      } else {
        const { text } = (await res.json()) as { text: string };
        if (action === "note") await onNoteGenerated(text);
        else if (action === "summarize") await onSummaryAppended(text);
        else await onNextStepsAppended(text);
      }
    } catch (err) {
      console.error(`AI ${action} failed:`, err);
      showError(t("failed"));
    } finally {
      setBusy(null);
    }
  };

  const Btn = ({
    icon: Icon,
    label,
    action,
    primary,
  }: {
    icon: typeof FileText;
    label: string;
    action: Action;
    primary?: boolean;
  }) => (
    <button
      type="button"
      onClick={() => void run(action)}
      disabled={busy !== null}
      className={
        primary
          ? "w-full flex items-center gap-2 px-3 py-2 rounded-[4px] bg-[var(--accent)] text-white text-xs hover:opacity-90 disabled:opacity-60 cursor-pointer"
          : "w-full flex items-center gap-2 px-3 py-2 rounded-[4px] border border-[var(--hairline)] bg-[var(--paper)] text-xs text-[var(--ink)] hover:bg-[rgba(12,26,51,0.03)] disabled:opacity-60 cursor-pointer"
      }
    >
      {busy === action ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <Icon
          size={12}
          className={primary ? "text-white" : "text-[var(--muted)]"}
        />
      )}
      <span className="text-left">{label}</span>
    </button>
  );

  return (
    <section className="bg-[var(--paper)] rounded-[4px] border border-[var(--hairline)] p-4 space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={14} className="text-[var(--accent)]" />
        <h3 className="ed-serif text-[15px] text-[var(--ink)]">{t("title")}</h3>
      </div>
      <Btn icon={BookOpen} label={t("generateSummary")} action="summary" />
      {clinicalTemplates && (
        <>
          {/* Clinical products may choose SOAP / BIRP / DAP / freeform. */}
          <div className="flex items-center gap-2 pt-1">
            <label
              htmlFor="note-format"
              className="ed-mono text-[10px] text-[var(--muted)] shrink-0"
            >
              {t("formatLabel")}
            </label>
            <select
              id="note-format"
              value={effectiveFormatId}
              onChange={(e) =>
                setOverrideFormatId(e.target.value as NoteFormatId)
              }
              disabled={busy !== null}
              className="flex-1 min-w-0 text-xs border border-[var(--hairline)] rounded-[4px] px-2 py-1.5 bg-[var(--paper)] text-[var(--ink)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] focus:border-[var(--accent)] disabled:opacity-60 cursor-pointer"
            >
              {NOTE_FORMAT_IDS.map((id) => (
                <option key={id} value={id}>
                  {NOTE_FORMATS[id].shortLabel[locale] ??
                    NOTE_FORMATS[id].shortLabel.en}
                </option>
              ))}
            </select>
          </div>
          <Btn icon={FileText} label={t("generateNote")} action="note" />
        </>
      )}
      <Btn icon={Layers} label={t("summarize")} action="summarize" primary />
      <Btn icon={ListChecks} label={t("nextSteps")} action="next-steps" />
      {clinicalTemplates && (
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-[4px] border border-[var(--hairline)] bg-[var(--paper-2)] text-xs text-[var(--faint)] cursor-not-allowed"
        >
          <Send size={12} className="text-[var(--faint)]" />
          <span className="text-left">{t("exportEhr")}</span>
        </button>
      )}
    </section>
  );
}
